/*
  Buy more, save more: buy any 2 items and get 10% off the entire cart, 3
  items 15%, 4 or more 20%. The discount itself is three automatic discounts in
  Shopify; this file only says it, in the same words everywhere, and keeps
  every line in step with the cart.

  The item count is read from /cart.js on arrival and again after every
  request that changes the cart, whoever sends it: the product page, quick
  add, the cart drawer, an app. It is done by watching fetch for the cart
  routes rather than by asking each script to report, so nothing can be
  missed.

  The free gift is not counted while it is free, so the count matches what
  the shopper chose herself.

  Every amount here is a preview, worked out in cents: Shopify spreads a
  percentage over the lines and can differ by a cent. The cart and the
  checkout show Shopify's own numbers.

  Elements it fills, by attribute:

    data-mm-box                       the block: data-mm-status on the
                                      dark bar, data-mm-text, and the
                                      progress bar's data-mm-stop="1|2|3|4"
    data-mm-line data-cents           the two lines on a product card,
                                      data-mm-pay and data-mm-pct; hidden
                                      while the cart is empty
    data-mm-item-line data-cents      the line under the product page price:
                                      the price with the cart, and what this
                                      item unlocks
    data-mm-price data-cents          the price with the cart's next tier,
                                      as in "Add to cart · $58.46"
    data-mm-look-title                the line over Complete the look, on
                                      the product page and the homepage

  Anything else listens for the mixmatch:update event on document, which
  carries the count and the cart.
*/
(() => {
  if (window.MixMatch) return;

  const config = window.mixMatchConfig || {};
  const giftHandle = config.giftHandle || '';
  const moneyFormat = config.moneyFormat || '${{amount}}';

  const pct = (n) => (n >= 4 ? 20 : n === 3 ? 15 : n === 2 ? 10 : 0);
  const unit = (cents, n) => Math.round((cents * (100 - pct(n))) / 100);

  /* Money in the shop's own format: $64.95, or $35 with whole is set. */
  function money(cents, whole) {
    const value = Math.max(0, Math.round(cents));
    const amount = (value / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDecimals = String(Math.round(value / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const shown = whole && value % 100 === 0 ? noDecimals : amount;

    return moneyFormat
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, shown.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, shown)
      .replace(/<[^>]*>/g, '');
  }

  /* The words, the same on every page, from the second
     Rosa-Mae-Buy-more-save-more.pdf. The discount is always "an extra
     X% off your entire cart", so it reads as coming on top of the sale
     price. -------------------------------------------------------------- */

  const sentence = (n) => {
    if (n <= 0) return 'Add any 2 items to unlock an extra 10% off your entire cart.';
    if (n >= 4) return 'That’s our best discount, on your entire cart.';
    return `Add 1 more item to unlock an extra ${pct(n + 1)}% off your entire cart.`;
  };

  /* The right of the dark bar once the cart has items. */
  const status = (n) => (n >= 2 ? `✓ Extra ${pct(n)}% off` : n === 1 ? '1 item in your cart' : '');

  /* The line under the product page price, in two parts: the bold price
     with this item in the cart, and what it unlocks. */
  const itemLine = (cents, n) => {
    if (n <= 0) return ['Buy any 2 items', ' · extra 10% off your entire cart ›'];
    const lead = `${money(unit(cents, n + 1))} with your cart`;
    if (n >= 4) return [lead, ' · extra 20% off ›'];
    return [lead, ` · unlocks an extra ${pct(n + 1)}% off ›`];
  };

  const lookTitle = (n) => {
    if (n <= 0) return 'Add 2 or more items to unlock an extra 10% off your entire cart.';
    if (n >= 4) return 'You get an extra 20% off your entire cart.';
    return `Add 1 more item to unlock an extra ${pct(n + 1)}% off your entire cart.`;
  };

  /* The cart's total at the tier its own count reaches, from unit prices. */
  const totalOf = (list) => {
    const n = list.length;
    return list.reduce((sum, cents) => sum + unit(cents, n), 0);
  };

  /* The cart ------------------------------------------------------------- */

  const isFreeGift = (item) => giftHandle && item.handle === giftHandle && item.final_line_price === 0;

  function countOf(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((sum, item) => (isFreeGift(item) ? sum : sum + item.quantity), 0);
  }

  /* One price per unit in the cart, before Mix & Match, for previews. */
  function unitsOf(cart) {
    const units = [];
    if (!cart || !cart.items) return units;
    cart.items.forEach((item) => {
      if (isFreeGift(item)) return;
      const each = item.original_price != null ? item.original_price : item.price;
      for (let i = 0; i < item.quantity; i += 1) units.push(each);
    });
    return units;
  }

  const state = {
    cart: null,
    count: Number.isFinite(config.count) ? config.count : 0,
  };

  /* The fill of the progress bar, in thirds of the track: one item
     reaches halfway to the first stop, so she sees she is on her way,
     then one third per tier. */
  const fillOf = (n) => (n >= 2 ? ((Math.min(n, 4) - 1) * 100) / 3 : n === 1 ? 100 / 6 : 0);

  /* One block, for a count: also used by the cart, on the tap, before
     Shopify has answered. With data-mm-preview set (How it works opened
     from the product page) a striped piece shows how far one more item
     takes her. */
  function renderBox(el, n) {
    const text = el.querySelector('[data-mm-text]');
    if (text) text.textContent = sentence(n);

    const label = el.querySelector('[data-mm-status]');
    if (label) {
      if (n > 0) {
        label.textContent = status(n);
      } else if (el.hasAttribute('data-mm-info-open')) {
        label.innerHTML = '<span class="mm-box__link">How it works ›</span>';
      } else {
        label.textContent = '';
      }
    }

    const nextTier = n < 2 ? 2 : n + 1;
    el.querySelectorAll('[data-mm-stop]').forEach((stop) => {
      const i = Number(stop.dataset.mmStop);
      stop.classList.toggle('is-have', n >= i);
      stop.classList.toggle('is-next', n < i && i === nextTier);
    });

    el.style.setProperty('--mm-fill', `${fillOf(n)}%`);
    const stripe = el.dataset.mmPreview ? fillOf(n + 1) - fillOf(n) : 0;
    el.classList.toggle('has-stripe', stripe > 0);
    el.style.setProperty('--mm-stripe', `${stripe}%`);
  }

  function render(root) {
    const scope = root || document;
    const n = state.count;

    scope.querySelectorAll('[data-mm-box]').forEach((el) => renderBox(el, n));

    /* The card's lines: this item at the tier the cart reaches with it,
       and the tier it unlocks. */
    scope.querySelectorAll('[data-mm-line]').forEach((el) => {
      el.hidden = n <= 0;
      const pay = el.querySelector('[data-mm-pay]');
      const off = el.querySelector('[data-mm-pct]');
      if (pay) pay.textContent = `${money(unit(Number(el.dataset.cents), n + 1))} with your cart`;
      if (off) off.textContent = n >= 4 ? 'Extra 20% off' : `Unlocks an extra ${pct(n + 1)}% off`;
    });

    scope.querySelectorAll('[data-mm-item-line]').forEach((el) => {
      const [lead, rest] = itemLine(Number(el.dataset.cents), n);
      const strong = document.createElement('strong');
      strong.textContent = lead;
      el.replaceChildren(strong, rest);
    });

    scope.querySelectorAll('[data-mm-price]').forEach((el) => {
      el.textContent = money(unit(Number(el.dataset.cents), n + 1));
    });

    scope.querySelectorAll('[data-mm-look-title]').forEach((el) => {
      el.textContent = lookTitle(n);
    });
  }

  let refreshing = null;

  async function refresh() {
    if (refreshing) return refreshing;

    refreshing = (async () => {
      try {
        const response = await fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        const cart = await response.json();
        state.cart = cart;
        state.count = countOf(cart);
        render();
        document.dispatchEvent(new CustomEvent('mixmatch:update', { detail: { count: state.count, cart } }));
      } catch (error) {
        // Offline or blocked: the lines keep what they last said.
      } finally {
        refreshing = null;
      }
    })();

    return refreshing;
  }

  /*
    Any request to a cart route that changes the cart is followed by a
    refresh. A short wait lets a burst of taps settle into one request.
  */
  let timer = null;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(refresh, 120);
  };

  const CHANGES = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const originalFetch = window.fetch;
  window.fetch = function mixMatchFetch(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const result = originalFetch.apply(this, arguments);
    if (CHANGES.test(url)) result.then(schedule, schedule);
    return result;
  };

  window.MixMatch = {
    pct,
    unit,
    money,
    totalOf,
    unitsOf,
    render,
    renderBox,
    refresh,
    get count() {
      return state.count;
    },
    get cart() {
      return state.cart;
    },
  };

  render();
  refresh();

  // Back from another page: the cart may have changed there.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
})();
